import {Alert, Box, Pagination, Paper, SxProps, Theme} from "@mui/material";
import {
    DataGrid,
    type GridColDef,
    GridFilterModel,
    type GridLocaleText,
    type GridPaginationModel,
    type GridRowSelectionModel,
    type GridSortModel,
    GridToolbar,
} from "@mui/x-data-grid";
import {deDE, enUS} from "@mui/x-data-grid/locales";
import {type DataGridPropsWithoutDefaultValue} from "@mui/x-data-grid/internals";
import {useTranslation} from "react-i18next";
import {QueryKey, useQuery,} from "react-query";
import {useState} from "react";
import type {DataLoader, DataRequestParams, DataRequestState, Filter, Sort} from "./types";
import {Identifiable} from "../../api";
import {datatableStyles} from "../../styles/datatable.styles";
import {SetFieldFn} from "./useDebouncedFilter";
import {MandantenFilter} from "../mandanten/table/FilterTableMandanten";
import {useUpdateSearchParam} from "../../hooks/useUpdateSearchParams";

const defaultPageSize = 25;

export type Props<TElement extends Identifiable, TFilter extends Filter> = {
    queryKey: QueryKey;
    loadData: DataLoader<TFilter, TElement>;
    columns: Array<GridColDef<TElement>>;
    request: DataRequestState<TFilter>;
    disableColumnFilter?: boolean;
    size?: "small" | "large";
    name?: string;
    initialFilters?: [];
    disableRowSelectionOnClick?: boolean;
    setField: SetFieldFn<MandantenFilter>;
    checkboxSelection?: boolean;
    rowSelectionModel?: GridRowSelectionModel;
    onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
    getRowId?: (row: TElement) => any;
    loading?: boolean;
    sx?: SxProps<Theme>;
} & Pick<DataGridPropsWithoutDefaultValue<TElement>, "onRowClick">;

export default function DataTable<
    TElement extends Identifiable,
    TFilter extends Filter
>({
      columns,
      queryKey,
      loadData,
      request: { params, sort, setPagination, setSort },
      ...props
  }: Props<TElement, TFilter>) {
    const results = useSearch({ queryKey, loadData, params });
    const updateSearchParam = useUpdateSearchParam();

    const [currentPage, setCurrentPage] = useState<number>(0);
    const [currentLimit, setCurrentLimit] = useState<number>(25);

    const handleSetPagination = (newPage: number, newLimit: number) => {
        setCurrentPage(newPage);
        setCurrentLimit(newLimit);
        setPagination(newPage, newLimit);
    };

    const handleFilterModelChange = (e: GridFilterModel) => {
        const serializedFilters = JSON.stringify(
            e.items.map((item) => ({
                field: item.field,
                operator: item.operator,
                value: item.value,
            }))
        );
        updateSearchParam("filter", serializedFilters);
        props.setField("filtered", serializedFilters);
    }

    const initialFilter = { items: props.initialFilters ?? [] };

    return (
        <Paper
            sx={datatableStyles.datatable.paper}
        >
            {results.error ? (
                <Alert severity="error">{(results.error as any).message}</Alert>
            ) : null}
            <DataGrid
                autoHeight
                getRowId={props.getRowId}
                initialState={{ filter: { filterModel: initialFilter }}}
                disableColumnFilter={props.disableColumnFilter}
                columns={columns}
                rows={results.data?.items ?? []}
                rowCount={results.data?.totalElements ?? 0}
                checkboxSelection={props.checkboxSelection}
                rowSelectionModel={props.rowSelectionModel}
                onRowSelectionModelChange={props.onRowSelectionModelChange}
                paginationMode="server"
                filterMode={"server"}
                onFilterModelChange={(e: GridFilterModel) => {handleFilterModelChange(e)}}
                paginationModel={toPaginationModel(currentPage, currentLimit)}
                onPaginationModelChange={(e) => {
                    if (results.isLoading) {
                        handleSetPagination(currentPage, currentLimit);
                    } else {
                        handleSetPagination(e.page, e.pageSize);
                    }
                }}
                sortingMode="server"
                sortModel={toSortModel(sort)}
                onSortModelChange={(e) => setSort(fromSortModel(e))}
                localeText={useLocaleText()}
                slots={{ toolbar: GridToolbar }}
                rowHeight={props.size?.toLowerCase() === "large" ? 55 : 38}
                {...props}
                loading={results.isLoading || props.loading}
                sx={{
                    '& .MuiDataGrid-row': {
                        cursor: 'pointer',
                        userSelect: 'none',
                    },
                    '& .MuiDataGrid-cell': {
                        userSelect: 'none',
                        '&:focus': {
                            outline: 'none'
                        }
                    },
                    ...props.sx
                }}
            />
            <Box
                sx={datatableStyles.datatable.tableBox}
            >
                <Pagination
                    count={Math.ceil((results.data?.totalElements ?? 0) / currentLimit)}
                    page={currentPage + 1}
                    onChange={(_event, page) => {
                        if (results.isLoading) {
                            handleSetPagination(currentPage - 1, currentLimit);
                        } else {
                            handleSetPagination(page - 1, currentLimit);
                        }
                    }}
                />
            </Box>
        </Paper>
    );
}

function toSortModel(sort?: Sort): GridSortModel {
    return (sort ?? []).map(({ field, direction }: {field: any, direction: any}) => ({
        field,
        sort: direction,
    }));
}

function fromSortModel(model: GridSortModel): Sort {
    return model.map(({ field, sort }) => ({ field, direction: sort ?? "asc" }));
}

function toPaginationModel(page?: number, limit?: number): GridPaginationModel {
    return { page: page ?? 0, pageSize: limit ?? defaultPageSize };
}

function useLocaleText(): Partial<GridLocaleText> {
    const { t: translation } = useTranslation("common");

    const userLocaleText = {
        noRowsLabel: translation("datatable-norows"),
    };

    const baseLocale = {
        ...enUS.components?.MuiDataGrid?.defaultProps?.localeText,
        ...(deDE.components?.MuiDataGrid?.defaultProps?.localeText ?? {}),
    };

    return {
        ...baseLocale,
        ...userLocaleText,
    };
}

function useSearch<TElement extends Identifiable, TFilter extends Filter>({
                                                                              queryKey,
                                                                              loadData,
                                                                              params,
                                                                          }: {
    queryKey: QueryKey;
    loadData: DataLoader<TFilter, TElement>;
    params: DataRequestParams<TFilter>;
}) {
    return useQuery(
        Array.isArray(queryKey) ? [...queryKey, params] : [queryKey, params],
        () => loadData(params),
        { suspense: false }
    );
}
